export interface RouteSeo {
  title: string;
  description: string;
}

/** One entry per route in config/site.ts `staticRoutes`. Kept centralized so titles/descriptions stay unique. */
export const routeSeo: Record<string, RouteSeo> = {
  "/": {
    title: "Devory — Graph-native software engineering platform",
    description:
      "Devory is a graph-native software engineering platform: components are composed, connected, tested, executed and published as one governed system.",
  },
  "/product": {
    title: "Product — Devory",
    description:
      "Every capability in Devory is an engineering component: composition, execution, qualification, deployment and distribution, each with an honest status.",
  },
  "/canvas": {
    title: "Canvas — Devory",
    description:
      "How Devory's visual engineering canvas connects components into graphs, and how the local Runtime executes them as real processes.",
  },
  "/components": {
    title: "Components — Devory",
    description:
      "The Devory component model: typed, versioned building blocks with real inputs, outputs and state, and what the foundation library does and does not include today.",
  },
  "/qualification": {
    title: "Testing and qualification — Devory",
    description:
      "How Devory components are tested, validated and integrity-checked before reuse, described at the level the platform actually implements.",
  },
  "/marketplace": {
    title: "Marketplace — Devory",
    description:
      "The public Devory Marketplace catalogue of verified components, collections, workflows and integrations, browsable today, with installation and commerce staged for later.",
  },
  "/agent-user-interface": {
    title: "For AI — Devory Autonomous User Interface & MCP",
    description:
      "Devory Autonomous User Interface (AUI) gives every AI agent its own accountable identity, scoped capabilities and audit trail, and how MCP connects an existing agent or host to it.",
  },
  "/docs": {
    title: "Docs — Devory",
    description:
      "What you can actually build with Devory today: the SDK, CLI, component model and AUI/MCP protocol surfaces, stated honestly against their real current state.",
  },
  "/cloud": {
    title: "Devory Cloud — Devory",
    description:
      "Devory Cloud's organisations, workspaces, shared libraries and deployment state: the shared canvas alongside local execution.",
  },
  "/solutions": {
    title: "Solutions — Devory",
    description:
      "How Devory applies to individual engineers, product and platform teams, AI-assisted teams, enterprises, creators and indie hackers, and beginners and students.",
  },
  "/pricing": {
    title: "Pricing — Devory",
    description:
      "Devory's six plans (Creator, Builder, Professional, Studio, Team and Enterprise): what each is for, who moves to the next one, and real self-serve checkout pricing.",
  },
  "/enterprise": {
    title: "Enterprise — Devory",
    description:
      "Organisation workspaces, roles and permissions, governance, auditability and deployment boundaries for enterprises evaluating Devory.",
  },
  "/security": {
    title: "Security — Devory",
    description:
      "Devory's security-by-design principles, current controls, controls under development, and how to report a vulnerability.",
  },
  "/about": {
    title: "About — Devory",
    description:
      "Devory's mission, what the platform is building, why it exists, and how to get in touch.",
  },
  "/contact": {
    title: "Contact Devory",
    description:
      "Reach Devory about product, private pilot interest, enterprise, support, security, privacy or legal questions.",
  },
  "/support": {
    title: "Support — Devory",
    description:
      "How to get help with Devory today, what support currently covers, and where to report issues.",
  },
  "/legal": {
    title: "Legal — Devory",
    description:
      "All current Devory policies in one place: Terms, Privacy, Refunds, Cancellation, Acceptable Use and Cookies.",
  },
  "/legal/terms": {
    title: "Terms of Service — Devory",
    description: "The Terms of Service governing use of the Devory website and platform.",
  },
  "/legal/privacy": {
    title: "Privacy Policy — Devory",
    description:
      "How Devory collects, uses and protects information from visitors and Devory Cloud customers.",
  },
  "/legal/refunds": {
    title: "Refund Policy — Devory",
    description: "How refunds are handled for paid Devory Cloud subscriptions.",
  },
  "/legal/cancellation": {
    title: "Cancellation Policy — Devory",
    description: "How to cancel a paid Devory Cloud subscription.",
  },
  "/legal/acceptable-use": {
    title: "Acceptable Use Policy — Devory",
    description: "Prohibited uses of Devory and how Devory enforces this policy.",
  },
  "/legal/cookies": {
    title: "Cookie Policy — Devory",
    description: "What cookies and local storage Devory's website currently uses.",
  },
};

export const notFoundSeo: RouteSeo = {
  title: "Page not found — Devory",
  description: "The page you requested does not exist on the Devory website.",
};
